import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaUser,
  FaSignOutAlt,
  FaWallet,
  FaClipboardList,
  FaLock,
  FaComments,
  FaArrowLeft,
  FaPhoneAlt,
  FaEnvelope,
  FaBuilding,
  FaBriefcase,
  FaEdit,
  FaSave,
  FaTimes,
  FaCamera,
  FaCheckCircle,
  FaShieldAlt,
  FaCar,
  FaMapMarkerAlt,
  FaIdCard
} from "react-icons/fa";
import MyComplaints from "../components/MyComplaints";
import { API_URL } from "../config/apiConfig";

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth || {});
  const [isLoading, setIsLoading] = useState(true);
  const [profileData, setProfileData] = useState({
    fullName: "",
    email: "",
    phone: "",
    firstName: "",
    lastName: "",
    gender: "",
    dateOfBirth: "",
    nationality: "",
    maritalStatus: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    passportNumber: "",
    passportExpiry: "",
    issuingCountry: "",
    panNumber: "",
    image: "",
    companyName: "",
    designation: "",
  });
  const [initialProfileData, setInitialProfileData] = useState(null);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [totalBookingsCount, setTotalBookingsCount] = useState(null);
  const [activeTab, setActiveTab] = useState("profile"); // profile, complaints

  const corporateToken = localStorage.getItem("corporateToken");

  useEffect(() => {
    if (!user && !corporateToken) {
      navigate("/login");
      return;
    }

    const fetchUserProfile = async () => {
      try {
        const token = user?.token || corporateToken;

        let data;
        if (corporateToken) {
          const response = await axios.get(`${API_URL}/corporate/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          data = response.data?.data || response.data?.user || {};
        } else {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          data = response.data?.data || response.data?.user || {};
        }

        if (corporateToken) {
          const nameParts = (data.fullName || "").split(" ");
          const firstName = nameParts[0] || "";
          const lastName = nameParts.slice(1).join(" ") || "";

          let companyName = data.companyName || data.company || "";
          if (data.corporateAccountId && !companyName) {
            try {
              const corpRes = await axios.get(`${API_URL}/corporate/me`, {
                headers: { Authorization: `Bearer ${corporateToken}` },
              });
              if (corpRes.data?.success) {
                companyName = corpRes.data.data?.companyName || corpRes.data.data?.company || "";
              }
            } catch (e) {
              console.error("Failed to fetch corporate details:", e);
            }
          }

          const formatted = {
            fullName: data.fullName || "",
            email: data.email || "",
            phone: data.mobileNumber || "",
            firstName: firstName,
            lastName: lastName,
            gender: data.gender || "",
            dateOfBirth: data.dob ? data.dob.split("T")[0] : "",
            nationality: data.nationality || "",
            maritalStatus: data.marital_status || "",
            address: data.address || "",
            city: data.city || "",
            state: data.state || "",
            pincode: data.pincode || "",
            passportNumber: data.passportNumber || "",
            passportExpiry: data.passportExpiry || "",
            issuingCountry: data.issuingCountry || "",
            panNumber: data.panNumber || "",
            image: data.image || "",
            companyName: companyName,
            designation: data.designation || "",
          };

          setProfileData(formatted);
          setInitialProfileData(formatted);

          if (data.image && data.image.startsWith("http")) {
            setImagePreview(data.image);
          }

          calculateProfileCompletion(data);
        } else {
          const nameParts = (data.name || "").split(" ");
          const firstName = nameParts[0] || "";
          const lastName = nameParts.slice(1).join(" ") || "";

          const formatted = {
            fullName: data.name || "",
            email: data.email || "",
            phone: data.phone || "",
            firstName: firstName,
            lastName: lastName,
            gender: data.gender || "",
            dateOfBirth: data.dob ? data.dob.split("T")[0] : "",
            nationality: data.nationality || "",
            maritalStatus: data.marital_status || "",
            address: data.address || "",
            city: data.city || "",
            state: data.state || "",
            pincode: data.pincode || "",
            passportNumber: data.passportNumber || "",
            passportExpiry: data.passportExpiry || "",
            issuingCountry: data.issuingCountry || "",
            panNumber: data.panNumber || "",
            image: data.image || "",
            companyName: "",
            designation: "",
          };

          setProfileData(formatted);
          setInitialProfileData(formatted);

          if (data.image && data.image.startsWith("http")) {
            setImagePreview(data.image);
          }

          calculateProfileCompletion(data);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        if (user) {
          setProfileData((prev) => ({
            ...prev,
            fullName: user.name || "",
            email: user.email || "",
            phone: user.phone || "",
          }));
        }
      }
    };

    const fetchWalletBalance = async () => {
      try {
        const token = user?.token;
        if (!corporateToken && token) {
          const endpoint = `${API_URL}/wallet/user/balance`;
          const response = await axios.get(endpoint, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.data?.success) {
            setWalletBalance(response.data.data?.balance || 0);
          }
        }
        if (corporateToken) {
          const endpoint = `${API_URL}/wallet/staff/balance`;
          const response = await axios.get(endpoint, {
            headers: { Authorization: `Bearer ${corporateToken}` },
          });
          if (response.data?.success) {
            setWalletBalance(response.data.data?.balance || 0);
          }
        }
      } catch (error) {
        console.error("Error fetching wallet balance:", error);
      }
    };

    const fetchBookingsCount = async () => {
      try {
        const token = user?.token || corporateToken;
        if (!token) return;
        const res = await axios.get(`${API_URL}/booking/my-bookings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.success && Array.isArray(res.data.data)) {
          setTotalBookingsCount(res.data.data.length);
        }
      } catch (e) {
        // quiet fallback
      }
    };

    fetchUserProfile();
    fetchWalletBalance();
    fetchBookingsCount();
    setIsLoading(false);
  }, [user, navigate, corporateToken]);

  const calculateProfileCompletion = (data) => {
    const fields = [
      data.name || data.fullName,
      data.email,
      data.phone || data.mobileNumber,
      data.gender,
      data.dob,
      data.city,
      data.state,
      data.address,
    ];
    const filledFields = fields.filter((f) => f && f.toString().trim()).length;
    setProfileCompletion(Math.round((filledFields / fields.length) * 100));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSizeInBytes = 20 * 1024 * 1024;
      if (file.size > maxSizeInBytes) {
        toast.error(`Image size must be less than 20MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        return;
      }

      if (!file.type.startsWith("image/")) {
        toast.error("Please upload a valid image file");
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCancelEdit = () => {
    if (initialProfileData) {
      setProfileData(initialProfileData);
      setImagePreview(initialProfileData.image || null);
    }
    setImageFile(null);
    setIsEditing(false);
  };

  const handleSaveProfile = async () => {
    try {
      setIsLoading(true);
      const token = user?.token || corporateToken;
      const formDataToSend = new FormData();
      const fullName = `${profileData.firstName} ${profileData.lastName}`.trim() || profileData.fullName;

      if (corporateToken) {
        formDataToSend.append("fullName", fullName);
      } else {
        formDataToSend.append("name", fullName);
      }

      formDataToSend.append("email", profileData.email || "");
      formDataToSend.append("address", profileData.address || "");
      formDataToSend.append("city", profileData.city || "");
      formDataToSend.append("state", profileData.state || "");
      formDataToSend.append("pincode", profileData.pincode || "");
      formDataToSend.append("nationality", profileData.nationality || "");
      formDataToSend.append("dob", profileData.dateOfBirth || "");
      formDataToSend.append("marital_status", profileData.maritalStatus || "");
      formDataToSend.append("gender", profileData.gender || "");

      if (corporateToken && profileData.designation) {
        formDataToSend.append("designation", profileData.designation);
      }

      if (imageFile) {
        formDataToSend.append("image", imageFile);
        toast.loading("Uploading image...");
      }

      const endpoint = corporateToken ? `/corporate/profile` : `/auth/edit-profile`;

      const response = await axios.put(`${API_URL}${endpoint}`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 150000,
      });

      if (response.data?.success) {
        toast.dismiss();
        toast.success("Profile updated successfully!");
        setIsEditing(false);
        setImageFile(null);

        const updatedData = response.data.data;
        const nameToSplit = updatedData.fullName || updatedData.name || fullName;
        const nameParts = nameToSplit.split(" ");
        const updatedFormatted = {
          ...profileData,
          fullName: nameToSplit,
          firstName: nameParts[0] || "",
          lastName: nameParts.slice(1).join(" ") || "",
          image: updatedData.image || profileData.image,
        };
        setProfileData(updatedFormatted);
        setInitialProfileData(updatedFormatted);

        if (updatedData.image && updatedData.image.startsWith("http")) {
          setImagePreview(updatedData.image);
        }
      }
    } catch (error) {
      toast.dismiss();
      console.error("Error updating profile:", error);
      toast.error(error.response?.data?.message || "Failed to update profile. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("corporateToken");
    localStorage.removeItem("corporateMember");
    navigate("/");
    toast.success("Logged out successfully!");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm font-semibold text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const userInitial = profileData.firstName
    ? profileData.firstName.charAt(0).toUpperCase()
    : profileData.fullName
    ? profileData.fullName.charAt(0).toUpperCase()
    : "U";

  const displayName = profileData.firstName || profileData.lastName
    ? `${profileData.firstName} ${profileData.lastName}`.trim()
    : profileData.fullName || "Valued Customer";

  return (
    <div className="bg-[#F8FAFC] min-h-screen pb-16 font-sans">
      {/* MOBILE STICKY HEADER */}
      <div className={`md:hidden sticky top-0 left-0 right-0 z-50 text-white shadow-md border-b ${
        corporateToken
          ? "bg-gradient-to-r from-red-800 to-rose-900 border-red-700"
          : "bg-gradient-to-r from-blue-900 to-indigo-900 border-blue-800"
      }`}>
        <div className="flex items-center px-4 py-3 gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-white hover:text-amber-300 transition-colors p-1 flex-shrink-0"
            aria-label="Go Back"
          >
            <FaArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="font-bold text-base leading-tight truncate">My Account</h1>
            <p className="text-xs text-blue-200 truncate">{displayName}</p>
          </div>
          <Link
            to="/wallet"
            className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 shadow-sm"
          >
            <FaWallet size={11} />
            <span>₹{walletBalance.toFixed(0)}</span>
          </Link>
        </div>
      </div>

      {/* HERO BANNER SECTION (CabZigo Theme Color) */}
      <div className={`relative text-white overflow-hidden border-b ${
        corporateToken
          ? "bg-gradient-to-r from-[#F56753] via-red-700 to-rose-900 border-red-600"
          : "bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 border-blue-700 shadow-md"
      }`}>
        {/* Glow Accent */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-10 left-10 w-72 h-72 bg-amber-400/15 rounded-full blur-2xl pointer-events-none"></div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          {/* Top navigation / Back link */}
          <div className="hidden md:flex justify-between items-center mb-6">
            <button
              type="button"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
              className="inline-flex items-center gap-2 text-white hover:text-white text-xs font-semibold bg-white/15 hover:bg-white/25 backdrop-blur-md px-3.5 py-1.5 rounded-full transition-all border border-white/20 shadow-sm"
            >
              <FaArrowLeft size={11} />
              <span>Back</span>
            </button>
            <div className="flex items-center gap-2 text-xs text-blue-100">
              <span>Account Status:</span>
              <span className="inline-flex items-center gap-1 text-emerald-300 font-semibold bg-emerald-500/20 px-2 py-0.5 rounded-full border border-emerald-400/30">
                <FaCheckCircle size={10} /> Active
              </span>
            </div>
          </div>

          {/* User Profile Card within Banner */}
          <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-5 text-center md:text-left">
              {/* Profile Avatar with Camera Overlay */}
              <div className="relative group">
                <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-gradient-to-tr from-amber-400 to-orange-500 p-1 shadow-xl flex-shrink-0">
                  <div className="w-full h-full rounded-[14px] bg-white flex items-center justify-center overflow-hidden">
                    {imagePreview ? (
                      <img src={imagePreview} alt={displayName} className="w-full h-full object-cover" />
                    ) : profileData.image ? (
                      <img src={profileData.image} alt={displayName} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-3xl md:text-4xl font-extrabold text-blue-600 tracking-wider">
                        {userInitial}
                      </span>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <label
                    htmlFor="profile-avatar-upload"
                    className="absolute -bottom-1 -right-1 bg-amber-500 hover:bg-amber-600 text-white p-2 rounded-xl shadow-lg cursor-pointer transition-transform hover:scale-110 border-2 border-white"
                    title="Upload profile photo"
                  >
                    <FaCamera size={13} />
                    <input
                      id="profile-avatar-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Name & Meta */}
              <div className="flex-1">
                <div className="flex items-center justify-center md:justify-start gap-2.5 flex-wrap">
                  <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                    {displayName}
                  </h1>
                  {corporateToken ? (
                    <span className="inline-flex items-center gap-1 bg-white/20 text-white text-xs font-semibold px-2.5 py-0.5 rounded-full border border-white/30">
                      Corporate Staff
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 bg-amber-400/25 text-amber-200 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-amber-300/30">
                      CabZigo Member
                    </span>
                  )}
                </div>

                {/* Subtitle / Details */}
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-y-2 gap-x-3 mt-3 text-xs md:text-sm text-blue-100">
                  {profileData.phone && (
                    <span className="inline-flex items-center gap-1.5 text-blue-50 bg-white/15 px-2.5 py-1 rounded-lg border border-white/20">
                      <FaPhoneAlt size={11} className="text-amber-300" />
                      <span>{profileData.phone}</span>
                    </span>
                  )}
                  {profileData.email && (
                    <span className="inline-flex items-center gap-1.5 text-blue-50 bg-white/15 px-2.5 py-1 rounded-lg border border-white/20">
                      <FaEnvelope size={11} className="text-blue-200" />
                      <span>{profileData.email}</span>
                    </span>
                  )}
                  {corporateToken && profileData.companyName && (
                    <span className="inline-flex items-center gap-1.5 text-blue-50 bg-white/15 px-2.5 py-1 rounded-lg border border-white/20">
                      <FaBuilding size={11} className="text-emerald-300" />
                      <span>{profileData.companyName}</span>
                    </span>
                  )}
                  {corporateToken && profileData.designation && (
                    <span className="inline-flex items-center gap-1.5 text-blue-50 bg-white/15 px-2.5 py-1 rounded-lg border border-white/20">
                      <FaBriefcase size={11} className="text-amber-200" />
                      <span>{profileData.designation}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* CTA Buttons in Hero Banner */}
            <div className="flex flex-row md:flex-col items-center md:items-end gap-3 w-full md:w-auto justify-center">
              <Link
                to="/wallet"
                className="flex-1 md:flex-none inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-4 py-2.5 rounded-xl shadow-lg shadow-orange-500/25 text-sm transition-all hover:scale-[1.02]"
              >
                <FaWallet size={14} />
                <span>Wallet: ₹{walletBalance.toFixed(2)}</span>
              </Link>
              <Link
                to="/"
                className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-all border border-white/25 shadow-sm"
              >
                <FaCar size={13} className="text-amber-300" />
                <span>Book New Ride</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* QUICK STATS CARDS (4-Columns) */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Card 1: Wallet Balance */}
          <Link
            to="/wallet"
            className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-all border border-slate-200/80 group"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Wallet Balance</span>
              <div className="w-8 h-8 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center group-hover:bg-orange-500 group-hover:text-white transition-colors">
                <FaWallet size={13} />
              </div>
            </div>
            <div className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
              ₹{walletBalance.toFixed(2)}
            </div>
            <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1 font-medium">
              <span className="text-orange-600 font-bold">Manage Balance</span> &rarr;
            </p>
          </Link>

          {/* Card 2: My Bookings */}
          <Link
            to="/bookings"
            className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-all border border-slate-200/80 group"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">My Rides</span>
              <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                <FaCar size={13} />
              </div>
            </div>
            <div className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
              {totalBookingsCount !== null ? `${totalBookingsCount} Rides` : "Ride History"}
            </div>
            <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1 font-medium">
              <span className="text-blue-600 font-bold">View All Trips</span> &rarr;
            </p>
          </Link>

          {/* Card 3: Profile Completion */}
          <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-200/80">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Profile Status</span>
              <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <FaCheckCircle size={13} />
              </div>
            </div>
            <div className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
              {profileCompletion}%
            </div>
            <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
              <div
                className="bg-gradient-to-r from-emerald-500 to-teal-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${profileCompletion}%` }}
              ></div>
            </div>
          </div>

          {/* Card 4: Security */}
          <Link
            to="/user/change-password"
            className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-all border border-slate-200/80 group"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Security</span>
              <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center group-hover:bg-purple-600 group-hover:text-white transition-colors">
                <FaShieldAlt size={13} />
              </div>
            </div>
            <div className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
              Password & PIN
            </div>
            <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1 font-medium">
              <span className="text-purple-600 font-bold">Update Password</span> &rarr;
            </p>
          </Link>
        </div>
      </div>

      {/* MAIN DASHBOARD CONTENT AREA */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* LEFT SIDEBAR NAVIGATION */}
          <div className="flex flex-col gap-4">
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200/80">
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-3 mb-2">
                Account Navigation
              </div>

              <nav className="flex flex-col space-y-1">
                <button
                  type="button"
                  onClick={() => setActiveTab("profile")}
                  className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-semibold transition-all ${
                    activeTab === "profile"
                      ? "bg-orange-50 text-orange-600 border border-orange-200/60 shadow-sm"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <FaUser className={activeTab === "profile" ? "text-orange-500" : "text-slate-400"} size={14} />
                  <span>Personal Profile</span>
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/bookings")}
                  className="w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all text-left"
                >
                  <FaClipboardList className="text-slate-400" size={14} />
                  <span>My Bookings</span>
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/wallet")}
                  className="w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all text-left"
                >
                  <FaWallet className="text-slate-400" size={14} />
                  <span>My Wallet</span>
                  <span className="ml-auto text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                    ₹{walletBalance.toFixed(0)}
                  </span>
                </button>

                {!corporateToken && (
                  <button
                    type="button"
                    onClick={() => setActiveTab("complaints")}
                    className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-semibold transition-all ${
                      activeTab === "complaints"
                        ? "bg-orange-50 text-orange-600 border border-orange-200/60 shadow-sm"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                  >
                    <FaComments className={activeTab === "complaints" ? "text-orange-500" : "text-slate-400"} size={14} />
                    <span>My Complaints</span>
                  </button>
                )}

                <Link
                  to="/user/change-password"
                  className="w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all"
                >
                  <FaLock className="text-slate-400" size={14} />
                  <span>Change Password</span>
                </Link>
              </nav>

              <div className="border-t border-slate-100 mt-4 pt-3">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                >
                  <FaSignOutAlt size={14} />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>

            {/* Quick Support Card */}
            <div className={`text-white rounded-2xl p-5 shadow-sm border ${
              corporateToken
                ? "bg-gradient-to-br from-red-800 to-rose-950 border-red-700"
                : "bg-gradient-to-br from-blue-700 to-blue-900 border-blue-600"
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base">🤝</span>
                <h4 className="font-bold text-sm">Need Ride Assistance?</h4>
              </div>
              <p className="text-xs text-blue-100 leading-relaxed mb-3">
                Our support team is available 24/7 to help with any bookings or queries.
              </p>
              <a
                href="tel:+919898121031"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-3 py-2 rounded-xl transition-colors w-full justify-center shadow-sm"
              >
                <FaPhoneAlt size={11} /> Call +91-9898121031
              </a>
            </div>
          </div>

          {/* RIGHT MAIN CONTENT AREA */}
          <div className="flex flex-col gap-6">
            {activeTab === "profile" && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-5 sm:p-7">
                {/* Header with Title & Action Buttons */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 border-b border-slate-100 gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Personal Information</h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Keep your details updated for a smooth booking experience
                    </p>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 transition-colors"
                        >
                          <FaTimes size={12} />
                          <span>Cancel</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveProfile}
                          className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl text-xs font-bold text-white bg-orange-500 hover:bg-orange-600 shadow-md shadow-orange-500/20 transition-all hover:scale-[1.02]"
                        >
                          <FaSave size={12} />
                          <span>Save Changes</span>
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 transition-colors"
                      >
                        <FaEdit size={12} />
                        <span>Edit Profile</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Profile Completion Card */}
                {profileCompletion < 100 && (
                  <div className="mt-5 bg-gradient-to-r from-orange-50/80 via-amber-50/60 to-orange-50/80 border border-orange-200/70 rounded-xl p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-orange-600 font-bold text-xs uppercase tracking-wider">
                          Profile {profileCompletion}% Complete
                        </span>
                      </div>
                      {!isEditing && (
                        <button
                          type="button"
                          onClick={() => setIsEditing(true)}
                          className="text-xs font-bold text-orange-600 hover:underline self-start sm:self-auto"
                        >
                          + Complete Profile Details
                        </button>
                      )}
                    </div>
                    <div className="w-full bg-orange-100/80 rounded-full h-1.5 mt-2.5 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-orange-500 to-amber-500 h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${profileCompletion}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-slate-600 mt-2">
                      Please add your email, full address, and date of birth for swift booking confirmations.
                    </p>
                  </div>
                )}

                {/* Profile Photo Uploader (visible in Edit mode) */}
                {isEditing && (
                  <div className="mt-6 bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-5 text-center">
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-3">
                      Profile Picture
                    </label>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                      <div className="w-20 h-20 rounded-2xl bg-slate-200 overflow-hidden flex-shrink-0 border-2 border-slate-300 shadow-sm">
                        {imagePreview ? (
                          <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                        ) : profileData.image ? (
                          <img src={profileData.image} alt="Current" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xl font-bold text-slate-400">
                            {userInitial}
                          </div>
                        )}
                      </div>
                      <div className="text-center sm:text-left">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="block text-xs text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-orange-50 file:text-orange-600 hover:file:bg-orange-100 cursor-pointer"
                        />
                        <p className="text-[11px] text-slate-400 mt-1.5">JPG, PNG or GIF up to 20MB</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* FORM FIELDS SECTIONS */}
                <form onSubmit={(e) => { e.preventDefault(); handleSaveProfile(); }} className="mt-6 space-y-6">
                  {/* SECTION 1: Personal Information */}
                  <div>
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <FaUser size={11} className="text-slate-400" />
                      <span>Basic Details</span>
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {corporateToken && (
                        <>
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                              Company Name <span className="text-slate-400 text-[10px]">(Fixed)</span>
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                name="companyName"
                                value={profileData.companyName}
                                disabled
                                placeholder="Company Name"
                                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-100 text-slate-500 text-sm font-medium cursor-not-allowed"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Designation</label>
                            <input
                              type="text"
                              name="designation"
                              value={profileData.designation}
                              onChange={handleInputChange}
                              disabled={!isEditing}
                              placeholder="e.g. Senior Manager"
                              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                                isEditing
                                  ? "bg-white border-slate-300 text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
                                  : "bg-slate-50 border-slate-200 text-slate-800 cursor-default"
                              }`}
                            />
                          </div>
                        </>
                      )}

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">First & Middle Name</label>
                        <input
                          type="text"
                          name="firstName"
                          value={profileData.firstName}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="First Name"
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                            isEditing
                              ? "bg-white border-slate-300 text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
                              : "bg-slate-50 border-slate-200 text-slate-800 cursor-default"
                          }`}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Last Name</label>
                        <input
                          type="text"
                          name="lastName"
                          value={profileData.lastName}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="Last Name"
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                            isEditing
                              ? "bg-white border-slate-300 text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
                              : "bg-slate-50 border-slate-200 text-slate-800 cursor-default"
                          }`}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Gender</label>
                        <select
                          name="gender"
                          value={profileData.gender}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                            isEditing
                              ? "bg-white border-slate-300 text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
                              : "bg-slate-50 border-slate-200 text-slate-800 cursor-default"
                          }`}
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Date of Birth</label>
                        <input
                          type="date"
                          name="dateOfBirth"
                          value={profileData.dateOfBirth}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                            isEditing
                              ? "bg-white border-slate-300 text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
                              : "bg-slate-50 border-slate-200 text-slate-800 cursor-default"
                          }`}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Marital Status</label>
                        <select
                          name="maritalStatus"
                          value={profileData.maritalStatus}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                            isEditing
                              ? "bg-white border-slate-300 text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
                              : "bg-slate-50 border-slate-200 text-slate-800 cursor-default"
                          }`}
                        >
                          <option value="">Select Marital Status</option>
                          <option value="Single">Single</option>
                          <option value="Married">Married</option>
                          <option value="Divorced">Divorced</option>
                          <option value="Widowed">Widowed</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Nationality</label>
                        <input
                          type="text"
                          name="nationality"
                          value={profileData.nationality}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="e.g. Indian"
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                            isEditing
                              ? "bg-white border-slate-300 text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
                              : "bg-slate-50 border-slate-200 text-slate-800 cursor-default"
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* SECTION 2: Contact Information */}
                  <div className="pt-4 border-t border-slate-100">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <FaPhoneAlt size={11} className="text-slate-400" />
                      <span>Contact Details</span>
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                          Mobile Number <span className="text-slate-400 text-[10px]">(Primary)</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={profileData.phone}
                          disabled
                          placeholder="Mobile Number"
                          className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-100 text-slate-600 text-sm font-medium cursor-not-allowed"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                          Email Address <span className="text-slate-400 text-[10px]">(For receipts & invoices)</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={profileData.email}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="your.email@example.com"
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                            isEditing
                              ? "bg-white border-slate-300 text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
                              : "bg-slate-50 border-slate-200 text-slate-800 cursor-default"
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* SECTION 3: Address & Location */}
                  <div className="pt-4 border-t border-slate-100">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <FaMapMarkerAlt size={11} className="text-slate-400" />
                      <span>Address & Location</span>
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="sm:col-span-3">
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Full Street Address</label>
                        <input
                          type="text"
                          name="address"
                          value={profileData.address}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="Apartment, Street name, Area"
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                            isEditing
                              ? "bg-white border-slate-300 text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
                              : "bg-slate-50 border-slate-200 text-slate-800 cursor-default"
                          }`}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">City</label>
                        <input
                          type="text"
                          name="city"
                          value={profileData.city}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="e.g. Mumbai"
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                            isEditing
                              ? "bg-white border-slate-300 text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
                              : "bg-slate-50 border-slate-200 text-slate-800 cursor-default"
                          }`}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">State</label>
                        <input
                          type="text"
                          name="state"
                          value={profileData.state}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="e.g. Maharashtra"
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                            isEditing
                              ? "bg-white border-slate-300 text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
                              : "bg-slate-50 border-slate-200 text-slate-800 cursor-default"
                          }`}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">PIN Code</label>
                        <input
                          type="text"
                          name="pincode"
                          value={profileData.pincode}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="e.g. 400001"
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                            isEditing
                              ? "bg-white border-slate-300 text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
                              : "bg-slate-50 border-slate-200 text-slate-800 cursor-default"
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Save button at bottom when editing */}
                  {isEditing && (
                    <div className="pt-6 border-t border-slate-100 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="px-5 py-2.5 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-orange-500 hover:bg-orange-600 shadow-md shadow-orange-500/20 transition-all hover:scale-[1.02]"
                      >
                        Save Profile Changes
                      </button>
                    </div>
                  )}
                </form>
              </div>
            )}

            {/* MY COMPLAINTS TAB */}
            {activeTab === "complaints" && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-5 sm:p-7">
                <MyComplaints />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
